import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useStore } from "../contexts/StoreContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  fetchMessages,
  fetchPasses,
  fetchIntegrations,
  fetchtTwilioPhoneNumbers,
  fetchIntegrationsEmailAddresses,
} from "../api/queries";
import { deleteMessage, createMessage, updateMessage } from "../api/mutations";
import { getMarketingTimeZone } from "../utils/marketingTimeZone";
import { utcIsoToZonedWallTime } from "../utils/zonedTime";

const initialFormState = {
  pass_id: "",
  message_type: "",
  scheduled_at: "",
  message: "",
  from: "",
  subject: "",
  integration_id: "",
};

/**
 * Custom hook for managing marketing data and operations
 *
 * Responsibilities:
 * - Fetching messages, passes, integrations data
 * - Managing state for form, modal, and alerts
 * - Providing CRUD operations for messages
 */
export const useMarketingData = () => {
  const { selectedStore } = useStore();
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  // UI State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [deleteMessageId, setDeleteMessageId] = useState(null);
  const [error, setError] = useState("");
  const [editingMessage, setEditingMessage] = useState(null);
  const [attemptedType, setAttemptedType] = useState("");

  // Form State
  const [formData, setFormData] = useState(initialFormState);

  // Integration IDs
  const [twilioIntegrationId, setTwilioIntegrationId] = useState(null);
  const [sendgridIntegrationId, setSendgridIntegrationId] = useState(null);
  const [mailgunIntegrationId, setMailgunIntegrationId] = useState(null);

  // Data Fetching - Messages
  const { data: messages = [], isLoading: isLoadingMessages } = useQuery({
    queryKey: ["messages", selectedStore?.id],
    queryFn: () => fetchMessages(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  // Data Fetching - Passes
  const { data: passes = [] } = useQuery({
    queryKey: ["passes", selectedStore?.id],
    queryFn: () => fetchPasses(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  // Data Fetching - Integrations
  const { data: integrations = [] } = useQuery({
    queryKey: ["integrations", selectedStore?.id],
    queryFn: () => fetchIntegrations(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  // Data Fetching - Twilio Phone Numbers
  const { data: phoneNumbers = {}, isLoading: isLoadingPhoneNumbers } =
    useQuery({
      queryKey: [
        "twilioPhoneNumbers",
        selectedStore?.id,
        formData.integration_id,
      ],
      queryFn: () =>
        fetchtTwilioPhoneNumbers(selectedStore?.id, formData.integration_id),
      enabled:
        !!selectedStore?.id &&
        !!formData.integration_id &&
        formData.message_type === "sms",
    });

  // Data Fetching - Integration Email Addresses
  const { data: emailAddresses = {}, isLoading: isLoadingEmailAddresses } =
    useQuery({
      queryKey: [
        "integrationsEmailAddresses",
        selectedStore?.id,
        formData.integration_id,
      ],
      queryFn: () =>
        fetchIntegrationsEmailAddresses(
          selectedStore?.id,
          formData.integration_id,
        ),
      enabled:
        !!selectedStore?.id &&
        !!formData.integration_id &&
        formData.message_type === "email",
    });

  // Integration Flags
  const hasSmsIntegration = integrations.some(
    (integration) => integration.tags && integration.tags.includes("sms"),
  );

  const hasEmailIntegration = integrations.some(
    (integration) => integration.tags && integration.tags.includes("email"),
  );

  // Effect to set integration IDs
  useEffect(() => {
    if (integrations.length > 0) {
      const twilioIntegration = integrations.find(
        (integration) => integration.integration_type === "twilio",
      );
      if (twilioIntegration) {
        setTwilioIntegrationId(twilioIntegration.id);
      } else {
        setTwilioIntegrationId(null);
      }

      const sendgridIntegration = integrations.find(
        (integration) => integration.integration_type === "sendgrid",
      );
      if (sendgridIntegration) {
        setSendgridIntegrationId(sendgridIntegration.id);
      } else {
        setSendgridIntegrationId(null);
      }

      const mailgunIntegration = integrations.find(
        (integration) => integration.integration_type === "mailgun",
      );
      if (mailgunIntegration) {
        setMailgunIntegrationId(mailgunIntegration.id);
      } else {
        setMailgunIntegrationId(null);
      }
    }
  }, [integrations]);

  // Mutations
  const createMessageMutation = useMutation({
    mutationFn: createMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["messages", selectedStore?.id],
      });
      handleCloseModal();
    },
  });

  const updateMessageMutation = useMutation({
    mutationFn: updateMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["messages", selectedStore?.id],
      });
      handleCloseModal();
    },
  });

  const deleteMessageMutation = useMutation({
    mutationFn: deleteMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["messages", selectedStore?.id],
      });
    },
  });

  // Handlers
  const handleOpenModal = (message = null) => {
    if (message) {
      setEditingMessage(message);

      // UTC scheduled_at -> wall time in the zone the form submits in
      // (MessageModal), so saving unchanged keeps the same instant.
      const { timeZone } = getMarketingTimeZone(selectedStore);

      setFormData({
        pass_id: message.pass ? message.pass.id.toString() : "all",
        message_type: message.message_type || "",
        scheduled_at: utcIsoToZonedWallTime(message.scheduled_at, timeZone),
        message: message.message || "",
        from: message.from || "",
        subject:
          message.subject ||
          message.emailSubject ||
          message.email_subject ||
          "",
        integration_id: message.integration_id
          ? message.integration_id.toString()
          : "",
      });
    } else {
      setEditingMessage(null);
      setFormData(initialFormState);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMessage(null);
    setFormData(initialFormState);
    setError("");
    setAttemptedType("");
  };

  const handleMessageTypeChange = (type) => {
    // If the type comes from an event, extract the value
    const newType = typeof type === "object" ? type.target.value : type;
    setAttemptedType(newType);

    if (newType === "sms") {
      if (hasSmsIntegration) {
        setError("");
        setFormData((prev) => ({
          ...prev,
          message_type: "sms",
          from: "", // Reset the from field when changing type
          integration_id: "", // Reset the integration_id when changing type
        }));
      } else {
        setError(t("marketing.errors.noSmsIntegration"));
        setFormData((prev) => ({
          ...prev,
          message_type: "",
          from: "",
          integration_id: "", // Reset the integration_id when changing type
        }));
      }
    } else if (newType === "email") {
      if (hasEmailIntegration) {
        setError("");
        setFormData((prev) => ({
          ...prev,
          message_type: "email",
          from: "", // Reset the from field when changing type
          integration_id: "", // Reset the integration_id when changing type
        }));
      } else {
        setError(t("marketing.errors.noEmailIntegration"));
        setFormData((prev) => ({
          ...prev,
          message_type: "",
          from: "",
          integration_id: "", // Reset the integration_id when changing type
        }));
      }
    } else {
      setError("");
      setFormData((prev) => ({
        ...prev,
        message_type: newType,
        from: "",
        integration_id: "", // Reset the integration_id when changing type
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if the event has updatedFormData (with UTC scheduled_at)
    let messageData = e.updatedFormData
      ? { ...e.updatedFormData }
      : { ...formData };

    // Ensure integration_id is properly formatted (should be a number, not a string)
    if (messageData.integration_id) {
      messageData.integration_id = parseInt(messageData.integration_id, 10);
    }

    if (editingMessage) {
      await updateMessageMutation.mutateAsync({
        storeId: selectedStore.id,
        messageId: editingMessage.id,
        messageData,
      });
    } else {
      await createMessageMutation.mutateAsync({
        storeId: selectedStore.id,
        messageData,
      });
    }
  };

  const handleDelete = async (messageId) => {
    setIsDeleteAlertOpen(true);
    setDeleteMessageId(messageId);
  };

  const handleConfirmDelete = async () => {
    if (deleteMessageId) {
      await deleteMessageMutation.mutateAsync({
        storeId: selectedStore.id,
        messageId: deleteMessageId,
      });
    }
    setIsDeleteAlertOpen(false);
    setDeleteMessageId(null);
  };

  // Define message types for form select options
  const messageTypes = [
    { value: "sms", label: "SMS" },
    { value: "email", label: "Email" },
    { value: "push", label: "Push" },
  ];

  return {
    // Data
    messages,
    passes,
    formData,
    setFormData,
    isModalOpen,
    isDeleteAlertOpen,
    editingMessage,
    error,
    setError,
    attemptedType,
    setAttemptedType,

    // Loading states
    isLoadingMessages,
    isLoadingPhoneNumbers,
    isLoadingEmailAddresses,

    // Integration data
    phoneNumbers,
    emailAddresses,
    hasSmsIntegration,
    hasEmailIntegration,
    twilioIntegrationId,
    sendgridIntegrationId,
    mailgunIntegrationId,
    messageTypes,

    // Handlers
    handleOpenModal,
    handleCloseModal,
    handleMessageTypeChange,
    handleSubmit,
    handleDelete,
    handleConfirmDelete,
    setIsDeleteAlertOpen,
  };
};
